import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { fmtNum, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Package, CircleNotch, ArrowsDownUp, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = { entrada: "Entrada", saida: "Saída", ajuste: "Acerto (contagem)" };

export default function Stock() {
  const [data, setData] = useState(null);
  const [moves, setMoves] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ product_id: "", type: "entrada", quantity: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([api.get("/stock"), api.get("/stock/movements")]);
      setData(s.data); setMoves(m.data.items);
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openMove = async (pid = "") => {
    setF({ product_id: pid, type: "entrada", quantity: "", notes: "" });
    setOpen(true);
    const { data: p } = await api.get("/products", { params: { limit: 500 } });
    setProducts(p.items);
  };
  const save = async () => {
    if (!f.product_id || f.quantity === "") return toast.error("Escolha o produto e a quantidade");
    setBusy(true);
    try {
      await api.post("/stock/move", { ...f, quantity: Number(f.quantity) });
      toast.success("Stock atualizado");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="stock-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Inventário</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Stock</h1>
        </div>
        <Button className="rounded-full" onClick={() => openMove()} data-testid="stock-move-btn"><ArrowsDownUp size={18} className="mr-1.5" /> Movimentar stock</Button>
      </div>
      {data?.low_count > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="stock-low-alert">
          <Warning size={18} weight="fill" className="text-amber-600" /> {data.low_count} produto(s) abaixo do stock mínimo
        </div>
      )}
      <div className="rounded-xl border border-border bg-card">
        {!data ? <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
          : data.items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <Package size={40} className="text-muted-foreground" />
              <p className="max-w-sm text-muted-foreground">Nenhum produto com controlo de stock. Ative “Controlar stock” num produto ou faça uma entrada.</p>
              <Button variant="outline" className="rounded-full" asChild><Link to="/produtos">Ir para Produtos</Link></Button>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Produto</TableHead><TableHead className="hidden md:table-cell">Categoria</TableHead>
                <TableHead className="text-right">Stock</TableHead><TableHead className="hidden md:table-cell text-right">Mínimo</TableHead><TableHead className="text-right">Ação</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((p) => (
                  <TableRow key={p.id} data-testid={`stock-row-${p.id}`}>
                    <TableCell className="font-medium">{p.name} {p.low && <Badge variant="outline" className="ml-2 border-amber-300 bg-amber-50 text-amber-800">Baixo</Badge>}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{p.category || "—"}</TableCell>
                    <TableCell className="text-right font-bold" data-testid={`stock-qty-${p.id}`}>{fmtNum(p.stock_qty)} {p.unit}</TableCell>
                    <TableCell className="hidden md:table-cell text-right text-muted-foreground">{fmtNum(p.min_stock)}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" className="rounded-full" onClick={() => openMove(p.id)} data-testid={`stock-move-${p.id}`}>Movimentar</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </div>
      {moves.length > 0 && (
        <div className="rounded-xl border border-border bg-card">
          <div className="border-b border-border px-5 py-3 font-display font-bold">Últimos movimentos</div>
          <ul className="divide-y divide-border">
            {moves.slice(0, 30).map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm">
                <span className="min-w-0 truncate"><b>{m.product_name}</b> · {TYPES[m.type]}{m.notes ? ` · ${m.notes}` : ""}</span>
                <span className="whitespace-nowrap text-muted-foreground">{m.type === "saida" ? "−" : m.type === "entrada" ? "+" : "="}{fmtNum(m.quantity)} · {fmtDate(m.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="stock-move-dialog">
          <DialogHeader><DialogTitle>Movimentar stock</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Produto</Label>
              <Select value={f.product_id} onValueChange={(v) => setF({ ...f, product_id: v })}>
                <SelectTrigger data-testid="stock-move-product"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Tipo</Label>
                <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                  <SelectTrigger data-testid="stock-move-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>{f.type === "ajuste" ? "Quantidade real" : "Quantidade"}</Label>
                <Input type="number" min="0" step="0.01" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="stock-move-qty" /></div>
            </div>
            <div className="space-y-1.5"><Label>Notas</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="stock-move-notes" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="stock-move-submit">{busy ? <CircleNotch className="animate-spin" /> : "Guardar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
